import type { Icon, IconWeight } from '@phosphor-icons/react';
import classNames from 'classnames';
import { m } from 'motion/react';
import { Fragment, type ReactElement } from 'react';

import CustomLink from '@/components/CustomLink';
import NoodleAI from '@/components/Icons/NoodleAI';
import ProgressIndicator from '@/components/ProgressIndicator';
import ShortcutKeys from '@/components/ShortcutKeys';

import s from './Header.module.scss';

/** Styles a header action as a compact icon-only button on mobile. */
export const mobileHeaderIconActionClassName = 'h-10 w-10 justify-center p-0 [&>svg]:m-0';

export type ActionType =
  | {
      componentBefore?: ReactElement;
      disabled?: boolean;
      href: string;
      isPrimary?: boolean;
      isSecondary?: boolean;
      isDestructive?: boolean;
      isAi?: boolean;
      label: string;
      shortcut?: string;
      onClick?: never;
      tooltip?: string;
      isFetching?: never;
      icon?: Icon;
      iconWeight?: IconWeight;
      iconAfter?: Icon;
      newTab?: boolean;
      className?: string;
      ariaLabel?: string;
    }
  | {
      componentBefore?: ReactElement;
      disabled?: boolean;
      href?: never;
      isPrimary?: boolean;
      isSecondary?: boolean;
      isDestructive?: boolean;
      isAi?: boolean;
      label: string;
      shortcut?: string;
      onClick?: () => void;
      tooltip?: string;
      isFetching?: boolean;
      icon?: Icon;
      iconWeight?: IconWeight;
      iconAfter?: Icon;
      newTab?: never;
      className?: string;
      ariaLabel?: string;
    };

const Action: React.FC<{ action: ActionType }> = ({ action }) => {
  if ('href' in action) {
    return (
      <CustomLink
        title={action.tooltip}
        to={action.href || null}
        className={classNames(
          action.isDestructive && s.action__destructive,
          action.isSecondary && s.action__secondary,
          (action.isPrimary || action.icon) && s.action__primary,
          action.isAi && s.action__ai,
          !action.isDestructive &&
            !action.isSecondary &&
            !action.icon &&
            !action.isPrimary &&
            !action.isAi &&
            s.action__secondary,
          action.className,
        )}
        newTab={action.newTab}
        ariaLabel={action.ariaLabel}
      >
        {action.icon && <action.icon size={16} weight={action.iconWeight} />}
        {action.label}
        {action.shortcut && (
          <ShortcutKeys
            value={action.shortcut}
            isWhite={action.isPrimary || (Boolean(action.icon) && !action.isSecondary)}
          />
        )}
        {action.iconAfter && <action.iconAfter size={16} weight={action.iconWeight} />}
      </CustomLink>
    );
  }

  if ('onClick' in action) {
    return (
      <m.button
        title={action.tooltip}
        onClick={() => action.onClick?.()}
        disabled={action.disabled}
        aria-label={action.ariaLabel}
        className={classNames(
          action.isDestructive && s.action__destructive,
          action.isSecondary && s.action__secondary,
          (action.isPrimary || action.icon) && s.action__primary,
          action.isAi && s.action__ai,
          !action.isDestructive &&
            !action.isSecondary &&
            !action.icon &&
            !action.isPrimary &&
            !action.isAi &&
            s.action__secondary,
          action.className,
        )}
        {...(action.isFetching &&
          (action.icon || action.isPrimary || action.isSecondary || action.isDestructive) && {
            style: {
              backgroundColor: 'var(--color-gray-25)',
              color: 'var(--color-gray-75)',
              pointerEvents: 'none',
            },
          })}
      >
        {action.isAi && (
          <>
            <div className={s.action__aiBorder} />
            <div className={s.action__aiBg} />
          </>
        )}
        {action.isFetching && <ProgressIndicator size={12} />}
        {action.componentBefore && !action.isFetching && action.componentBefore}
        {action.icon && !action.isFetching && <action.icon size={16} weight={action.iconWeight} />}
        <span className={classNames(action.isAi ? s.action__aiChildren : s.contents)}>
          {action.isAi && <NoodleAI size={16} />}
          {action.label}
          {action.shortcut && (
            <ShortcutKeys
              value={action.shortcut}
              isWhite={action.isPrimary || (Boolean(action.icon) && !action.isSecondary)}
            />
          )}
        </span>
        {action.iconAfter && <action.iconAfter size={16} weight={action.iconWeight} />}
      </m.button>
    );
  }

  return (
    <m.div title={action.tooltip} className={classNames(s.action__static, action.className)}>
      {action.componentBefore && !action.isFetching && action.componentBefore}
      {action.icon && !action.isFetching && <action.icon size={16} weight={action.iconWeight} />}
      {action.label}
      {action.iconAfter && <action.iconAfter size={16} weight={action.iconWeight} />}
    </m.div>
  );
};

export default Action;
